import { angularInjectorProvider } from 'Injector';
import { useLocation, useParams } from 'react-router-dom';
import { hasAdminAccess, hasSuperEditorOrInterviewerAccess, type CurrentUser } from 'Users';

import { type FrontRoyalRootScope, useSuspenseCurrentUser } from 'FrontRoyalAngular';
import { type AccessPolicy, type AccessPolicyProps } from './ProtectedRoute.types';
import { buildSignInPathWithTarget } from './buildSignInPathWithTarget';

const SIGN_IN = 'SIGN_IN';
const HOME = 'HOME';

export function getRequiresNotLoggedInRedirect({ currentUser }: { currentUser: CurrentUser | null }) {
    if (currentUser) return HOME;
    return null;
}

export function getRequiresProfileConfirmationRedirect({ currentUser }: { currentUser: CurrentUser | null }) {
    if (!currentUser) return SIGN_IN;
    if (currentUser.confirmedProfileInfo) return HOME;

    return null;
}

export function getHasAccessRedirect({
    currentUser,
    accessPolicy,
}: {
    currentUser: CurrentUser | null;
    accessPolicy: AccessPolicy;
}) {
    if (accessPolicy === 'hasAdminAccess' && hasAdminAccess(currentUser)) return null;
    if (accessPolicy === 'hasSuperEditorOrInterviewerAccess' && hasSuperEditorOrInterviewerAccess(currentUser))
        return null;
    if (accessPolicy === 'hasLearnerAccess' && currentUser) return null;
    return SIGN_IN;
}

export function useAccessPolicy({ accessPolicy, accessPolicyFn }: AccessPolicyProps) {
    const injector = angularInjectorProvider.requireInjector();
    const $rootScope = injector.get<FrontRoyalRootScope>('$rootScope');
    const currentUser = useSuspenseCurrentUser();
    const params = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    let redirect: string | null = null;

    switch (accessPolicy) {
        case 'notLoggedIn':
            redirect = getRequiresNotLoggedInRedirect({ currentUser });
            break;
        case 'requiresProfileConfirmation':
            redirect = getRequiresProfileConfirmationRedirect({ currentUser });
            break;
        case 'hasAdminAccess':
        case 'hasSuperEditorOrInterviewerAccess':
        case 'hasLearnerAccess':
            redirect = getHasAccessRedirect({ currentUser, accessPolicy });
            break;
        case 'custom':
            if (!currentUser || !accessPolicyFn({ currentUser, params, queryParams })) {
                redirect = SIGN_IN;
            }
            break;
        default:
            // FIXME
            break;
        // throw new Error(`Invalid access policy: ${accessPolicy}`);
    }

    if (redirect === SIGN_IN) redirect = buildSignInPathWithTarget($rootScope.postLogoutSignInPath, location);
    if (redirect === HOME) redirect = $rootScope.homePath || '/dashboard';

    return redirect;
}
